import type { SRT_SortingState } from '@/shared/components/master-table'
import type { FilterRuleModel, OptimizationIDModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useMemo, useState } from 'react'
import { AlertTriangleIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { useStatisticQuery } from '@/entities/optimization-statistic'

import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { DeltaValue } from '@/shared/components/ui/delta-value'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

const emptyArray: [] = []

type AverageValuesRow = {
	brand: string
	statistics_average_init_price: number
	statistics_average_final_price: number
}
export default function AverageValuesWidget({
	queryParams,
	isLoading,
}: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const repricingStatisticQuery = useStatisticQuery<AverageValuesRow[]>(
		{
			...queryParams!,
			currency,
			metrics: [
				'statistics_average_init_price',
				'statistics_average_final_price',
			],
			dimensions: ['brand'],
		},
		{
			enabled: !!queryParams && !isLoading,
			select: (data): AverageValuesRow[] =>
				zipSeries<AverageValuesRow>(data.meta, data.data),
		}
	)
	const rows = repricingStatisticQuery.data || emptyArray
	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			id: 'name',
			desc: true,
		},
	])
	const columns = useMemo(() => getColumns(t, currency), [currency, t])
	const table = useMasterReactTable<AverageValuesRow>({
		data: rows,
		columns,
		state: {
			sorting,
		},
		enableColumnActions: false,
		enableVirtualization: true,
		layoutMode: 'grid',
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		onSortingChange: setSorting,
	})
	if (repricingStatisticQuery.isFetching || isLoading) {
		return <LoadingPlaceholder />
	}
	if (repricingStatisticQuery.isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{repricingStatisticQuery.error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}
	if (!rows?.length) {
		return (
			<div className='border-base flex max-h-[350px] w-full flex-col overflow-hidden rounded-lg border border-solid py-5'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<SearchIcon />
						</EmptyMedia>
						<EmptyDescription>{t('no_data')}</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</div>
		)
	}

	return (
		<div className='border-base flex max-h-[350px] w-full flex-col overflow-hidden rounded-lg border border-solid'>
			<MasterTable table={table} />
		</div>
	)
}
const columnHelper = createSRTColumnHelper<AverageValuesRow>()
const getColumns = (t: TFunction, currency?: string) => [
	columnHelper.accessor('brand', {
		header: t('general_name'),
		Cell: ({ cell }) => cell.getValue(),
	}),
	columnHelper.accessor('statistics_average_init_price', {
		size: 110,
		header: t('before', { defaultValue: 'Before' }),
		Cell: ({ cell }) =>
			formatMoney(cell.getValue(), currency, NumberFormats.Fixed),
	}),
	columnHelper.accessor('statistics_average_final_price', {
		size: 120,
		header: t('after', { defaultValue: 'After' }),
		Cell: ({ cell }) =>
			formatMoney(cell.getValue(), currency, NumberFormats.Fixed),
	}),
	columnHelper.accessor(
		(row) =>
			(row.statistics_average_final_price / row.statistics_average_init_price) *
				100 -
			100,
		{
			id: 'delta',
			size: 80,
			header: '∆',
			Cell: ({ cell }) => {
				const value = cell.getValue()

				return (
					<DeltaValue value={value}>
						<div className='cell-data-number'>
							{formatNumber(value, NumberFormats.PercentSigned)}
						</div>
					</DeltaValue>
				)
			},
		}
	),
]

function LoadingPlaceholder() {
	return (
		<div className='border-base flex max-h-[350px] w-full flex-col overflow-hidden rounded-lg border border-solid p-3'>
			<div className='flex flex-col items-center space-y-2'>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-20' />
					<Skeleton className='h-5 w-10' />
				</div>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-24' />
					<Skeleton className='h-5 w-12' />
				</div>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-28' />
					<Skeleton className='h-5 w-12' />
				</div>
			</div>
		</div>
	)
}
